interface FireConfettiOptions {
  particleCount?: number;
  angle?: number;
  spread?: number;
  startVelocity?: number;
  decay?: number;
  gravity?: number;
  drift?: number;
  ticks?: number;
  origin?: { x: number; y: number };
  shapes?: ('square' | 'circle')[];
  scalar?: number;
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

interface Fetti {
  x: number;
  y: number;
  wobble: number;
  wobbleSpeed: number;
  velocity: number;
  angle2D: number;
  tiltAngle: number;
  color: RGB;
  shape: 'square' | 'circle';
  tick: number;
  totalTicks: number;
  decay: number;
  drift: number;
  random: number;
  tiltSin: number;
  tiltCos: number;
  wobbleX: number;
  wobbleY: number;
  gravity: number;
  ovalScalar: number;
  scalar: number;
}

const DEFAULTS: Required<FireConfettiOptions> = {
  particleCount: 50,
  angle: 90,
  spread: 45,
  startVelocity: 45,
  decay: 0.9,
  gravity: 1,
  drift: 0,
  ticks: 200,
  origin: { x: 0.5, y: 0.5 },
  shapes: ['square', 'circle'],
  scalar: 1,
};

const COLORS_HEX = ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff'];

const Z_INDEX = 9999;

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let fettis: Fetti[] = [];
let rafId: number | null = null;
let resizeBound = false;

const hexToRgb = (hex: string): RGB => {
  const clean = hex.replace(/[^0-9a-f]/gi, '');
  let value: string;
  if (clean.length >= 6) {
    value = clean.slice(0, 6);
  } else {
    const short = clean.padEnd(3, '0').slice(0, 3);
    const a = short[0] ?? '0';
    const b = short[1] ?? '0';
    const c = short[2] ?? '0';
    value = `${a}${a}${b}${b}${c}${c}`;
  }
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
};

const COLORS_RGB = COLORS_HEX.map(hexToRgb);

const handleResize = () => {
  if (!canvas) return;
  canvas.width = document.documentElement.clientWidth;
  canvas.height = document.documentElement.clientHeight;
};

const ensureCanvas = () => {
  if (canvas) return;
  canvas = document.createElement('canvas');
  canvas.style.position = 'fixed';
  canvas.style.top = '0';
  canvas.style.left = '0';
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = String(Z_INDEX);
  canvas.width = document.documentElement.clientWidth;
  canvas.height = document.documentElement.clientHeight;
  document.body.appendChild(canvas);
  ctx = canvas.getContext('2d');
  if (!resizeBound) {
    window.addEventListener('resize', handleResize);
    resizeBound = true;
  }
};

const teardown = () => {
  if (canvas?.parentNode) canvas.parentNode.removeChild(canvas);
  canvas = null;
  ctx = null;
  rafId = null;
};

const createConfetti = (
  startX: number,
  startY: number,
  options: Required<FireConfettiOptions>
): Fetti => {
  const radAngle = options.angle * (Math.PI / 180);
  const radSpread = options.spread * (Math.PI / 180);
  const color: RGB = COLORS_RGB[Math.floor(Math.random() * COLORS_RGB.length)] ??
    COLORS_RGB[0] ?? { r: 0, g: 0, b: 0 };
  const shape: 'square' | 'circle' =
    options.shapes[Math.floor(Math.random() * options.shapes.length)] ??
    options.shapes[0] ??
    'square';

  return {
    x: startX,
    y: startY,
    wobble: Math.random() * 10,
    wobbleSpeed: Math.min(0.11, Math.random() * 0.1 + 0.05),
    velocity: options.startVelocity * 0.5 + Math.random() * options.startVelocity,
    angle2D: -radAngle + (0.5 * radSpread - Math.random() * radSpread),
    tiltAngle: (Math.random() * (0.75 - 0.25) + 0.25) * Math.PI,
    color,
    shape,
    tick: 0,
    totalTicks: options.ticks,
    decay: options.decay,
    drift: options.drift,
    random: Math.random() + 2,
    tiltSin: 0,
    tiltCos: 0,
    wobbleX: 0,
    wobbleY: 0,
    gravity: options.gravity * 3,
    ovalScalar: 0.6,
    scalar: options.scalar,
  };
};

const updateFetti = (context: CanvasRenderingContext2D, fetti: Fetti): boolean => {
  fetti.x += Math.cos(fetti.angle2D) * fetti.velocity + fetti.drift;
  fetti.y += Math.sin(fetti.angle2D) * fetti.velocity + fetti.gravity;
  fetti.velocity *= fetti.decay;

  fetti.wobble += fetti.wobbleSpeed;
  fetti.wobbleX = fetti.x + 10 * fetti.scalar * Math.cos(fetti.wobble);
  fetti.wobbleY = fetti.y + 10 * fetti.scalar * Math.sin(fetti.wobble);

  fetti.tiltAngle += 0.1;
  fetti.tiltSin = Math.sin(fetti.tiltAngle);
  fetti.tiltCos = Math.cos(fetti.tiltAngle);
  fetti.random = Math.random() + 2;

  const progress = fetti.tick++ / fetti.totalTicks;

  const x1 = fetti.x + fetti.random * fetti.tiltCos;
  const y1 = fetti.y + fetti.random * fetti.tiltSin;
  const x2 = fetti.wobbleX + fetti.random * fetti.tiltCos;
  const y2 = fetti.wobbleY + fetti.random * fetti.tiltSin;

  context.fillStyle = `rgba(${fetti.color.r}, ${fetti.color.g}, ${fetti.color.b}, ${1 - progress})`;
  context.beginPath();

  if (fetti.shape === 'circle') {
    context.ellipse(
      fetti.x,
      fetti.y,
      Math.abs(x2 - x1) * fetti.ovalScalar,
      Math.abs(y2 - y1) * fetti.ovalScalar,
      (Math.PI / 10) * fetti.wobble,
      0,
      2 * Math.PI
    );
  } else {
    context.moveTo(Math.floor(fetti.x), Math.floor(fetti.y));
    context.lineTo(Math.floor(fetti.wobbleX), Math.floor(y1));
    context.lineTo(Math.floor(x2), Math.floor(y2));
    context.lineTo(Math.floor(x1), Math.floor(fetti.wobbleY));
  }

  context.closePath();
  context.fill();

  return fetti.tick < fetti.totalTicks;
};

const tick = () => {
  if (!canvas || !ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  fettis = fettis.filter((f) => updateFetti(ctx!, f));

  if (fettis.length > 0) {
    rafId = requestAnimationFrame(tick);
  } else {
    teardown();
  }
};

const fire = (options: FireConfettiOptions = {}) => {
  if (typeof window === 'undefined') return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

  const merged: Required<FireConfettiOptions> = {
    ...DEFAULTS,
    ...options,
    origin: { ...DEFAULTS.origin, ...(options.origin ?? {}) },
  };

  ensureCanvas();
  if (!canvas) return;

  const startX = canvas.width * merged.origin.x;
  const startY = canvas.height * merged.origin.y;

  for (let i = 0; i < merged.particleCount; i++) {
    fettis.push(createConfetti(startX, startY, merged));
  }

  if (rafId === null) {
    rafId = requestAnimationFrame(tick);
  }
};

export const fireConfetti = () => {
  const isDesktop = window.matchMedia?.('(min-width: 768px)').matches ?? true;

  if (isDesktop) {
    fire({ particleCount: 80, spread: 70, angle: 60, origin: { x: 0, y: 0.6 } });
    fire({ particleCount: 80, spread: 70, angle: 120, origin: { x: 1, y: 0.6 } });
    setTimeout(() => {
      fire({ particleCount: 50, spread: 100, angle: 90, origin: { x: 0.5, y: 0.4 } });
    }, 300);
  } else {
    fire({ particleCount: 90, spread: 100, angle: 90, origin: { x: 0.5, y: 0.4 } });
  }
};
